import React, { useEffect, useState } from 'react';
import { useMatchStore } from '../../stores/matchStore';
import { Match } from '../../types/match';
import { MATCH_RESULT_LABELS } from '../../utils/constants';
import { 
  TrophyIcon, 
  HandRaisedIcon, 
  CalendarIcon, 
  UserGroupIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface MatchHistoryProps {
  className?: string;
  limit?: number;
  showLoadMore?: boolean;
}

const MatchHistory: React.FC<MatchHistoryProps> = ({ 
  className = '',
  limit,
  showLoadMore = true
}) => {
  const { 
    matchHistory, 
    isLoading, 
    error, 
    hasMoreHistory,
    loadMatchHistory, 
    loadMoreHistory,
    clearError
  } = useMatchStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResult, setSelectedResult] = useState<string>('all');
  const [expandedMatch, setExpandedMatch] = useState<string | null>(null);

  useEffect(() => {
    if (matchHistory.length === 0) {
      loadMatchHistory();
    }
  }, []);

  const handleLoadMore = () => {
    if (!isLoading && hasMoreHistory) {
      loadMoreHistory();
    }
  };

  const handleRefresh = () => {
    loadMatchHistory(0);
  };

  const toggleMatchExpansion = (matchId: string) => {
    setExpandedMatch(expandedMatch === matchId ? null : matchId);
  };

  // 过滤比赛记录
  const filteredMatches = matchHistory.filter(match => {
    // 按结果过滤
    if (selectedResult !== 'all' && match.result !== selectedResult) {
      return false;
    }

    // 按搜索词过滤
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const allPlayers = [...match.pairing.team1, ...match.pairing.team2];
      const hasPlayerMatch = allPlayers.some(player => 
        player.name.toLowerCase().includes(searchLower)
      );
      
      if (!hasPlayerMatch) {
        return false;
      }
    }

    return true;
  });

  // 限制显示数量
  const displayMatches = limit ? filteredMatches.slice(0, limit) : filteredMatches;

  const getResultIcon = (result: string) => {
    switch (result) {
      case 'win':
        return <TrophyIcon className="w-5 h-5 text-green-600" />;  // 我方获胜 - 奖杯绿色
      case 'draw':
        return <HandRaisedIcon className="w-5 h-5 text-yellow-600" />; // 平局 - 握手黄色
      case 'loss':
        return <XMarkIcon className="w-5 h-5 text-red-600" />;     // 我方失败 - X红色
      default:
        return null;
    }
  };

  const getResultColor = (result: string) => {
    switch (result) {
      case 'win':
        return 'text-green-600 bg-green-50';  // 我方获胜 - 绿色
      case 'draw':
        return 'text-yellow-600 bg-yellow-50'; // 平局 - 黄色
      case 'loss':
        return 'text-red-600 bg-red-50';       // 我方失败 - 红色
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getResultDescription = (result: string) => {
    switch (result) {
      case 'win':
        return '我方获胜';
      case 'draw':
        return '平局';
      case 'loss':
        return '我方失败';
      default:
        return '未知结果';
    }
  };

  if (isLoading && matchHistory.length === 0) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">比赛历史</h2>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          刷新
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={clearError}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 搜索和过滤 */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="搜索玩家姓名..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <select
          value={selectedResult}
          onChange={(e) => setSelectedResult(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">所有结果</option>
          <option value="win">我方获胜</option>
          <option value="draw">平局</option>
          <option value="loss">我方失败</option>
        </select>
      </div>

      {/* 统计信息 */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-800">
              {matchHistory.length}
            </div>
            <div className="text-sm text-gray-600">总比赛数</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">
              {matchHistory.filter(m => m.result === 'win').length}
            </div>
            <div className="text-sm text-gray-600">我方获胜</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-600">
              {matchHistory.filter(m => m.result === 'draw').length}
            </div>
            <div className="text-sm text-gray-600">平局</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">
              {matchHistory.filter(m => m.result === 'loss').length}
            </div>
            <div className="text-sm text-gray-600">我方失败</div>
          </div>
        </div>
      </div>

      {/* 比赛列表 */}
      {displayMatches.length > 0 ? (
        <div className="space-y-4">
          {displayMatches.map((match) => (
            <MatchCard
              key={match.id}
              match={match}
              isExpanded={expandedMatch === match.id}
              onToggleExpansion={() => toggleMatchExpansion(match.id)}
              getResultIcon={getResultIcon}
              getResultColor={getResultColor}
              getResultDescription={getResultDescription}
            />
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          <UserGroupIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg mb-2">
            {searchTerm || selectedResult !== 'all' ? '没有找到匹配的比赛' : '暂无比赛记录'}
          </p>
          <p className="text-sm">
            {searchTerm || selectedResult !== 'all' ? '尝试调整搜索条件' : '开始第一场比赛吧！'}
          </p>
        </div>
      )}

      {/* 加载更多按钮 */}
      {showLoadMore && hasMoreHistory && !limit && (
        <div className="text-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2 mx-auto"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>加载中...</span>
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-5 h-5" />
                <span>加载更多</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* 显示限制提示 */}
      {limit && filteredMatches.length > limit && (
        <div className="text-center mt-4 text-sm text-gray-500">
          显示最近 {limit} 场比赛，共 {filteredMatches.length} 场
        </div>
      )}
    </div>
  );
};

interface MatchCardProps {
  match: Match;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  getResultIcon: (result: string) => React.ReactNode;
  getResultColor: (result: string) => string;
  getResultDescription: (result: string) => string;
}

const MatchCard: React.FC<MatchCardProps> = ({
  match,
  isExpanded,
  onToggleExpansion,
  getResultIcon,
  getResultColor,
  getResultDescription
}) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggleExpansion}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* 结果图标 */}
            <div className={`p-2 rounded-lg ${getResultColor(match.result)}`}>
              {getResultIcon(match.result)}
            </div>

            {/* 比赛信息 */}
            <div>
              <div className="font-semibold text-gray-800">
                {getResultDescription(match.result)}
              </div>
              <div className="text-sm text-gray-600 flex items-center space-x-2">
                <CalendarIcon className="w-4 h-4" />
                <span>{new Date(match.playedAt).toLocaleString('zh-CN')}</span>
              </div>
            </div>
          </div>

          {/* 展开按钮 */}
          <ChevronDownIcon 
            className={`w-5 h-5 text-gray-400 transition-transform ${
              isExpanded ? 'transform rotate-180' : ''
            }`} 
          />
        </div>

        {/* 简要配对信息 */}
        <div className="mt-3 text-sm text-gray-600">
          <span className="font-medium">参赛团队：</span>
          <span className="font-medium">
            {[...match.pairing.team1, ...match.pairing.team2].map(p => p.name).join(' & ')}
          </span>
        </div>
      </div>

      {/* 展开内容 */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 详细配对信息 */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">比赛配对</h4>
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-blue-800 font-medium mb-1">队伍 1</div>
                  <div className="space-y-1">
                    {match.pairing.team1.map((player) => (
                      <div key={player.id} className="text-sm">
                        {player.name} (#{player.number})
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="text-green-800 font-medium mb-1">队伍 2</div>
                  <div className="space-y-1">
                    {match.pairing.team2.map((player) => (
                      <div key={player.id} className="text-sm">
                        {player.name} (#{player.number})
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 队列快照 */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">当时队列状态</h4>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="space-y-2">
                  {match.queueSnapshot.map((player, index) => (
                    <div 
                      key={player.id} 
                      className={`text-sm flex items-center justify-between p-2 rounded ${
                        index < 4 
                          ? 'bg-green-50 text-green-800' 
                          : 'bg-orange-50 text-orange-800'
                      }`}
                    >
                      <span>{player.name} (#{player.number})</span>
                      <span className="text-xs">
                        {index < 4 ? '参赛' : '休息'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchHistory;